import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, MapPin, Phone, Calendar, Clock, Compass } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiError } from "@/shared/lib/api";
import { useAuth } from "@/shared/context/AuthContext";
import AddressInput from "@/shared/components/AddressInput";

/**
 * Waitlist — the rich application form that gates booking for new
 * riders. Submitted via POST /api/me/waitlist. On success the rider
 * is routed to /waitlist/status which renders the "You're on the
 * waitlist" surface from the same backend doc.
 *
 * Approved riders (existing users + admin-approved waitlist users)
 * are redirected straight to / so they never see this form.
 */
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const FREQUENCY_OPTIONS = [
  "Every week",
  "Several times per week",
  "Occasionally",
  "Just exploring",
];
const METHOD_OPTIONS = [
  "I drive myself",
  "Uber / Lyft",
  "Friend or family",
  "Public transportation",
  "No reliable transportation",
];

function makeQuarterHourWindows() {
  // Full 24-hour day in 15-min increments — 96 windows including the
  // 11:45 PM → 12:00 AM close. Link serves overnight, manufacturing,
  // healthcare, and warehouse riders, so the entire clock is available.
  // Returns [{ value: "00:00-00:15", label: "12:00 AM – 12:15 AM" }, …]
  const out = [];
  for (let h = 0; h < 24; h += 1) {
    for (let m = 0; m < 60; m += 15) {
      const startH = h, startM = m;
      let endH = h, endM = m + 15;
      if (endM === 60) { endM = 0; endH = h + 1; }
      const pad = (n) => String(n).padStart(2, "0");
      // 23:45 → 00:00 closes the day; everything past it would belong to
      // tomorrow and is skipped.
      if (endH === 24 && endM > 0) continue;
      const endHForVal = endH === 24 ? 0 : endH;
      const value = `${pad(startH)}:${pad(startM)}-${pad(endHForVal)}:${pad(endM)}`;
      const fmt = (hh, mm) => {
        if (hh === 24) return `12:${pad(mm)} AM`;
        const ampm = hh >= 12 ? "PM" : "AM";
        const h12 = ((hh + 11) % 12) + 1;
        return `${h12}:${pad(mm)} ${ampm}`;
      };
      out.push({ value, label: `${fmt(startH, startM)} – ${fmt(endH, endM)}` });
    }
  }
  return out;
}

export default function Waitlist() {
  const nav = useNavigate();
  const { user, loading: authLoading, refreshUser } = useAuth();
  const windows = useMemo(makeQuarterHourWindows, []);

  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [phone, setPhone] = useState("");
  const [home, setHome] = useState("");
  const [dest, setDest] = useState("");
  const [days, setDays] = useState([]);
  const [pickupWindow, setPickupWindow] = useState("");
  const [returnWindow, setReturnWindow] = useState("");
  const [frequency, setFrequency] = useState("");
  const [method, setMethod] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Prefill from authed user when available.
  useEffect(() => {
    if (!user) return;
    if (!first && user.first_name) setFirst(user.first_name);
    if (!last && user.last_name) setLast(user.last_name);
    if (!phone && user.phone) setPhone(user.phone);
    if (!home && user.home_address) setHome(user.home_address);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Approved users → straight to the map. (Existing riders + post-approval.)
  useEffect(() => {
    if (authLoading) return;
    if (!user) { nav("/login"); return; }
    if (user.access_status === "APPROVED") { nav("/"); return; }
    // If a prior application already exists, jump to status.
    (async () => {
      try {
        const { data } = await api.get("/me/waitlist");
        if (data?.application) nav("/waitlist/status");
      } catch (_) { /* first-time visitor — fine */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]);

  const toggleDay = (d) => setDays((prev) => prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]);

  const valid =
    first.trim().length > 0 &&
    last.trim().length > 0 &&
    phone.trim().length >= 7 &&
    home.trim().length > 3 &&
    dest.trim().length > 3 &&
    days.length > 0 &&
    pickupWindow &&
    returnWindow &&
    frequency &&
    method;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!valid || submitting) return;
    setSubmitting(true);
    try {
      await api.post("/me/waitlist", {
        first_name: first.trim(),
        last_name: last.trim(),
        phone: phone.trim(),
        home_address: home.trim(),
        destination_address: dest.trim(),
        days_needed: days,
        pickup_window: pickupWindow,
        return_window: returnWindow,
        frequency,
        current_method: method,
      });
      await refreshUser?.();
      toast.success("You're on the waitlist");
      nav("/waitlist/status", { replace: true });
    } catch (ex) {
      toast.error(formatApiError(ex?.response?.data?.detail) || "Could not submit your application");
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading || !user) return null;

  return (
    <div className="fixed inset-0 flex flex-col bg-[#F9F7F4]" data-testid="waitlist-page">
      <div className="flex-1 min-h-0 overflow-y-auto wheel-scroll">
        <div
          className="max-w-2xl mx-auto px-5 sm:px-8 pb-[max(env(safe-area-inset-bottom),96px)]"
          style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 84px)" }}
        >
          <div className="text-[10px] tracking-[0.26em] uppercase font-medium text-[#ed6500] mb-3">
            Welcome to Link
          </div>
          <h1 className="font-serif text-[34px] sm:text-[42px] leading-[1.05] tracking-tight text-[#0A1128]">
            Tell us where you travel.
          </h1>
          <p className="text-[15px] text-[#515E7B] mt-3 max-w-prose">
            We&rsquo;ll notify you when Link becomes available on your route.
          </p>

          <form onSubmit={submit} className="mt-10 space-y-9">
            <Section title="About you" icon={Phone}>
              <div className="grid grid-cols-2 gap-3">
                <Field label="First name" value={first} onChange={setFirst} testId="wl-first" />
                <Field label="Last name"  value={last}  onChange={setLast}  testId="wl-last" />
              </div>
              <Field label="Phone" value={phone} onChange={setPhone} placeholder="(555) 555-1234" testId="wl-phone" />
            </Section>

            <Section title="Where you go" icon={MapPin}>
              <FieldLabel>Where would we pick you up?</FieldLabel>
              <AddressInput
                value={home}
                onChange={setHome}
                placeholder="Home address"
                allowCurrentLocation
                showSavedPlaces={false}
                testId="wl-home"
              />
              <FieldLabel>Where do you travel regularly?</FieldLabel>
              <AddressInput
                value={dest}
                onChange={setDest}
                placeholder="Work, doctor, school, gym, airport…"
                allowCurrentLocation={false}
                showSavedPlaces={false}
                testId="wl-dest"
              />
            </Section>

            <Section title="When you need transportation" icon={Calendar}>
              <FieldLabel>What days do you typically need a ride?</FieldLabel>
              <div className="grid grid-cols-7 gap-1.5" data-testid="wl-days">
                {DAYS.map((d) => {
                  const active = days.includes(d);
                  return (
                    <button
                      key={d}
                      type="button"
                      onClick={() => toggleDay(d)}
                      className={`aspect-square rounded-xl border text-[11px] font-semibold tracking-[0.16em] uppercase ${
                        active
                          ? "bg-[#ed6500] border-[#ed6500] text-white"
                          : "bg-white border-[#E5E1D8] text-[#515E7B] hover:border-[#ed6500]"
                      }`}
                      data-testid={`wl-day-${d}`}
                    >
                      {d.charAt(0)}
                    </button>
                  );
                })}
              </div>

              <FieldLabel>When would you typically like to be picked up?</FieldLabel>
              <WindowSelect value={pickupWindow} onChange={setPickupWindow} options={windows} testId="wl-pickup-window" />

              <FieldLabel>When should we typically be ready for you?</FieldLabel>
              <WindowSelect value={returnWindow} onChange={setReturnWindow} options={windows} testId="wl-return-window" />
            </Section>

            <Section title="A little more" icon={Compass}>
              <FieldLabel>How often do you need transportation?</FieldLabel>
              <ChipGroup
                options={FREQUENCY_OPTIONS}
                value={frequency}
                onChange={setFrequency}
                testId="wl-frequency"
              />
              <FieldLabel>How do you get around today?</FieldLabel>
              <ChipGroup
                options={METHOD_OPTIONS}
                value={method}
                onChange={setMethod}
                testId="wl-method"
              />
            </Section>

            <button
              type="submit"
              disabled={!valid || submitting}
              className="w-full h-14 rounded-2xl bg-[#0A1128] hover:bg-[#0A1128]/95 text-white text-[12px] tracking-[0.28em] uppercase font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              data-testid="wl-submit"
            >
              {submitting ? (
                <><Loader2 size={14} className="animate-spin" /> Submitting</>
              ) : (
                <>Join the waitlist <ArrowRight size={14} /></>
              )}
            </button>
            <p className="text-[11.5px] text-[#515E7B] text-center">
              You can explore routes + see pricing now. Booking unlocks after we approve your application.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2 text-[10px] tracking-[0.24em] uppercase font-medium text-[#0A1128]">
        <Icon size={12} strokeWidth={1.8} className="text-[#ed6500]" />
        {title}
      </div>
      {children}
    </section>
  );
}

function FieldLabel({ children }) {
  return (
    <div className="mt-4 mb-1 text-[10px] tracking-[0.2em] uppercase font-medium text-[#515E7B]">
      {children}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testId }) {
  return (
    <label className="block">
      <span className="block mb-1 text-[10px] tracking-[0.2em] uppercase font-medium text-[#515E7B]">{label}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || label}
        className="w-full h-12 bg-white border border-[#E5E1D8] rounded-xl px-4 text-[15px] text-[#0A1128] placeholder:text-[#515E7B]/45 focus:outline-none focus:border-[#ed6500] focus:ring-2 focus:ring-[#ed6500]/15 transition-all"
        data-testid={testId}
      />
    </label>
  );
}

function WindowSelect({ value, onChange, options, testId }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-12 bg-white border border-[#E5E1D8] rounded-xl pl-4 pr-10 text-[15px] text-[#0A1128] appearance-none focus:outline-none focus:border-[#ed6500] focus:ring-2 focus:ring-[#ed6500]/15 transition-all"
        data-testid={testId}
      >
        <option value="">Select a 15-minute window…</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <Clock size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#515E7B]/60 pointer-events-none" />
    </div>
  );
}

function ChipGroup({ options, value, onChange, testId }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testId}>
      {options.map((opt) => {
        const active = value === opt;
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            className={`h-11 px-4 rounded-full text-[13px] font-medium transition-all ${
              active
                ? "bg-[#0A1128] text-white"
                : "bg-white border border-[#E5E1D8] text-[#515E7B] hover:border-[#ed6500]"
            }`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}
